"use client";
import React, { useState, useRef, useMemo, useEffect } from "react";
import "./SelectionChips.css";

/**
 * CarTopInfo + SelectionChips – geteilte Komponenten fuer die Step-Flow-Seiten.
 * Zeigt im Info-Slot entweder den Subtitle (textNode) oder die Auswahl-Chips
 * (Marke, Modell, ...) und faded sauber mit der Step-Animation.
 *
 * Verbatim aus der Startseite extrahiert, damit alle Flow-Seiten exakt das
 * gleiche Verhalten haben (Single Source of Truth).
 */

/* ------------------------- CarTopInfo ------------------------- */
export function CarTopInfo({
  items,
  textNode,
  shouldAnimate,
  animateOut,
  animateIn,
  hidden,
  // Opt-in (z.B. auto-verkaufen): haelt den Subtitle als unsichtbaren Platzhalter
  // im DOM, sobald Chips gezeigt werden -> der Info-Slot behaelt die (hoehere)
  // Subtitle-Hoehe, statt auf die kleinere Chip-Hoehe zu schrumpfen. So springt
  // der Hero beim Wechsel Subtitle -> Marke nicht hoch/runter. Responsive, ohne
  // feste Pixel-Hoehe (der echte Subtitle-Text definiert die Hoehe pro Breite).
  reserveTextSpace,
}) {
  const [showChips, setShowChips] = React.useState(items?.length > 0);
  const [snapshot, setSnapshot] = React.useState(items || []);

  React.useEffect(() => {
    if (items?.length > 0) {
      setSnapshot(items);
      setShowChips(true);
      return;
    }
    // items leer -> der Subtitle-Text soll erscheinen.
    // WICHTIG (Codex-Fix): den aktuellen Inhalt (Chip-Snapshot) während JEDES
    // aktiven Fade-Outs behalten – unabhängig von shouldAnimate. Beim "Zurück"
    // löscht getBackStep die Marke (items=[]) BEVOR nextStepRef/shouldAnimateInfo
    // gesetzt sind; ohne diesen Guard würde der Subtitle hier bei voller Deckkraft
    // ohne fade-out/is-hidden rendern -> der "kommt -> weg -> zurück"-Snap.
    if (animateOut && !hidden) return;
    setShowChips(false);
  }, [items, animateOut, hidden]);

  return (
    <div
      className={[
        "Car-Top-Info",
        reserveTextSpace ? "Car-Top-Info--reserve" : "",
        reserveTextSpace && showChips ? "Car-Top-Info--chips" : "",
        shouldAnimate && animateOut ? "fade-out" : "",
        shouldAnimate && animateIn ? "fade-in" : "",
        hidden ? "is-hidden" : "", // 👈 neu
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {reserveTextSpace ? (
        <>
          {/* Subtitle bleibt im DOM (haelt die Hoehe); via CSS unsichtbar, sobald
              Chips da sind. Subtitle + Chips liegen im selben Grid-Slot (gestapelt). */}
          {textNode}
          {showChips ? <SelectionChips items={snapshot} /> : null}
        </>
      ) : showChips ? (
        <SelectionChips items={snapshot} />
      ) : (
        textNode
      )}
    </div>
  );
}

/* ---------------------- SelectionChips ----------------------- */
export function SelectionChips({ items }) {
  const isInitialMount = useRef(true);
  const EXIT_MS = 280;

  const [chipMap, setChipMap] = useState(() => {
    const m = new Map();
    (items ?? []).forEach((i) => m.set(i.key, { item: i, status: "idle" }));
    return m;
  });

  const TOP_KEYS = useMemo(() => new Set(["brand", "model", "var", "mt"]), []);

  const formatValue = (key, value) => {
    if (value == null) return "";
    const str = String(value).trim();

    if (key === "doors") {
      const n = Number(str);
      return Number.isFinite(n) ? `${n} Türen` : `${str} Türen`;
    }
    if (key === "ps") {
      const psMatch = str.match(/(\d+)\s*PS/i);
      if (psMatch) return `${psMatch[1]} PS`;
      const kwMatch = str.match(/(\d+)\s*kW/i);
      if (kwMatch) {
        const kw = parseInt(kwMatch[1], 10);
        if (Number.isFinite(kw)) {
          const ps = Math.round(kw * 1.35962);
          return `${ps} PS`;
        }
      }
      if (/^\d+$/.test(str)) return `${str} PS`;
      return str;
    }
    return str;
  };

  useEffect(() => {
    const newKeys = new Set(items?.map((i) => i.key) ?? []);
    setChipMap((prev) => {
      const next = new Map(prev);

      (items ?? []).forEach((it) => {
        const got = next.get(it.key);
        if (!got) {
          next.set(it.key, {
            item: it,
            status: isInitialMount.current ? "idle" : "enter",
          });
        } else {
          next.set(it.key, {
            item: it,
            status: got.status === "exit" ? "exit" : "idle",
          });
        }
      });

      for (const [key, val] of next) {
        if (!newKeys.has(key) && val.status !== "exit") {
          next.set(key, { ...val, status: "exit" });
          setTimeout(() => {
            setChipMap((curr) => {
              const c = new Map(curr);
              const v = c.get(key);
              if (!v || v.status !== "exit") return curr;
              c.delete(key);
              return c;
            });
          }, EXIT_MS);
        }
      }
      return next;
    });

    isInitialMount.current = false;
  }, [items]);

  if ((items?.length ?? 0) === 0 && chipMap.size === 0) return null;

  const all = Array.from(chipMap.values());
  const top = all.filter(({ item }) => TOP_KEYS.has(item.key));
  const bottom = all.filter(({ item }) => !TOP_KEYS.has(item.key));

  const renderChip = ({ item, status }, idx) => {
    const cls = [
      "chip",
      status === "enter" ? "chip--enter" : "",
      status === "exit" ? "chip--exit" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const style =
      status === "exit"
        ? { animationDuration: `${EXIT_MS}ms`, animationDelay: `${idx * 20}ms` }
        : undefined;

    return (
      <span
        className={cls}
        role="listitem"
        key={item.key}
        data-row={TOP_KEYS.has(item.key) ? "top" : "bottom"}
        style={style}
      >
        <span className="chip-value">{formatValue(item.key, item.value)}</span>
      </span>
    );
  };

  return (
    <div className="SelectedChips" role="list">
      <div
        className="SelectedChipsRow SelectedChipsRow--top"
        role="group"
        aria-label="Auswahl – Hauptangaben"
      >
        {top.map(renderChip)}
      </div>
      <div
        className="SelectedChipsRow SelectedChipsRow--bottom"
        role="group"
        aria-label="Auswahl – Details"
      >
        {bottom.map(renderChip)}
      </div>
    </div>
  );
}
